import { SxProps, Theme } from "@mui/material";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { formField, menu, signer } from "theme/colors";

const m = formFieldMetrics;

/**
 * `AccountMemberPicker` restyled onto the app's shared systems: the field is
 * the 44px form-field box every `CustomInput` / select uses, and the popup is
 * the same card and row rhythm as every `Menu` (`theme/colors.ts` `menu`).
 *
 * `CustomAutoComplete` paints its own (older) chrome on the inner TextField
 * with `!important`, so these rules go one class deeper
 * (`.MuiTextField-root .MuiOutlinedInput-root`) to win on specificity.
 */
const accountMemberPickerStyles = {
  field: {
    "&.MuiAutocomplete-root, &.MuiAutocomplete-root:hover": {
      bgcolor: "transparent",
    },
    "& .MuiTextField-root .MuiOutlinedInput-root": {
      height: m.height,
      // Room on the right for the clear + popup icons.
      p: `0 56px 0 ${m.paddingX} !important`,
      borderRadius: m.radius,
      bgcolor: formField.bg,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: formField.border,
        borderWidth: "1px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: formField.border,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: formField.focusBorder,
        borderWidth: "1px",
      },
    },
    "& .MuiTextField-root .MuiOutlinedInput-root .MuiAutocomplete-input": {
      p: "0 !important",
      fontSize: m.fontSize,
      fontWeight: m.fontWeight,
      color: formField.text,
      "&::placeholder": {
        color: formField.placeholder,
        opacity: 1,
        fontWeight: m.fontWeight,
      },
    },
    "& .MuiAutocomplete-endAdornment": {
      right: "10px",
      "& .MuiSvgIcon-root": { color: formField.adornment },
    },
    // `CustomAutoComplete` types its `sx` without a theme, hence the bare `SxProps`.
  } as SxProps,

  /** The popup card — identical to every app menu. */
  paper: {
    mt: "4px",
    border: `1px solid ${menu.border}`,
    borderRadius: menu.radius,
    boxShadow: menu.shadow,
    backgroundImage: "none",
    "& .MuiAutocomplete-listbox": {
      p: menu.padding,
    },
    "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
      minHeight: "auto",
      p: menu.itemPadding,
      borderRadius: menu.itemRadius,
      "&:hover, &.Mui-focused": {
        bgcolor: menu.itemHoverBg,
      },
      '&[aria-selected="true"], &[aria-selected="true"].Mui-focused': {
        bgcolor: `${menu.itemSelectedBg} !important`,
      },
    },
    "& .MuiAutocomplete-loading, & .MuiAutocomplete-noOptions": {
      p: menu.itemPadding,
      fontSize: menu.itemFontSize,
      color: signer.text.meta,
    },
  } as SxProps<Theme>,

  optionText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  } as SxProps<Theme>,

  optionName: {
    fontSize: menu.itemFontSize,
    fontWeight: menu.itemFontWeight,
    color: menu.itemText,
    lineHeight: 1.45,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  optionEmail: {
    fontSize: "12px",
    fontWeight: 500,
    color: signer.text.meta,
    lineHeight: 1.4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,
};

export default accountMemberPickerStyles;
